import { createContext, useContext } from 'react';

interface SidebarCtxState {
  /**
   * If the sidebar is expanded or collapsed
   *
   * - For mobile sidebars, this will always be `true`
   * - For non-mobile sidebars, this will be the expanded state of the sidebar
   */
  isExpanded: boolean;
  /**
   * If the sidebar is in mobile mode (i.e. rendered on a mobile device)
   */
  isMobile: boolean;
  /**
   * If the sidebar is open on mobile
   *
   * - For mobile sidebars, this will be the open state of the sidebar
   * - For non-mobile sidebars, this will always be `false`
   */
  isOpenMobile: boolean;
}

interface SidebarCtxMethods {
  /**
   * Set the current expanded state of the sidebar
   */
  setIsExpanded: (expanded: boolean) => void;
  /**
   * Set the current open state of the mobile sidebar
   */
  setIsOpenMobile: (open: boolean) => void;
  /**
   * Toggle the current state of the sidebar
   *
   * - For mobile sidebars, this will toggle the `isOpenMobile` state
   * - For non-mobile sidebars, this will toggle the `isExpanded` state
   */
  toggleSidebar: () => void;
}

export interface SidebarCtx extends SidebarCtxState, SidebarCtxMethods {}

// ============================================================
//     CONTEXT
// ============================================================
export const SidebarContext = createContext<SidebarCtx | null>(null);

export const useSidebarCtx = () => {
  const context = useContext(SidebarContext);

  if (!context) {
    throw new Error('`useSidebarCtx` can only be used within a `<Sidebar>` component');
  }

  return context;
};
